/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import SearchIcon from '@material-ui/icons/Search';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { baseScreen } from 'hoc';
import { useHistory, useParams } from 'react-router';
import i18n from 'i18n-js';
import moment from 'moment';
import { useStores } from 'hooks';
import ComponentWithLoading from 'src/components/ComponentWithLoading';
import { EmsDoctorDto } from 'src/stores/backend/EmsStore/types';
import { Doctor } from '../doctors/types';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto',
  },
  card: {
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    marginBottom: theme.spacing(3),
  },
  image: {
    maxWidth: '100%',
    maxHeight: 450,
    objectFit: 'contain',
    display: 'block',
    margin: '0 auto',
    borderRadius: 6,
    border: '1px solid #e0e0e0',
  },
  sectionTitle: {
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  emsSearchBox: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    '& th': {
      fontWeight: 600,
    },
  },
  syndicateChip: {
    fontWeight: 600,
    backgroundColor: '#e8f4fd',
    color: '#0288d1',
  },
  approveButton: {
    width: 300,
    height: 48,
    fontSize: '1rem',
    fontWeight: 600,
  },
}));

const SyndicateIdVerificationComponent: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams() as any;
  const {
    backend: {
      doctors: { approveSyndicateID, getOne },
      ems,
    },
  } = useStores();

  const [data, setData] = useState<Doctor>();
  const [emsQuery, setEmsQuery] = useState('');
  const [emsResults, setEmsResults] = useState<EmsDoctorDto[]>([]);
  const [isSearchingEms, setIsSearchingEms] = useState(false);
  const [emsSearched, setEmsSearched] = useState(false);

  useEffect(() => {
    const load = async () => {
      const res = await getOne(id);
      setData(res);
      const initialQuery = res?.arabicName || res?.syndicateNumber || res?.user?.name || '';
      setEmsQuery(initialQuery);
      if (initialQuery) {
        searchEms(initialQuery);
      }
    };
    load();
  }, [id]);

  const searchEms = async (queryToSearch: string) => {
    const q = (queryToSearch || emsQuery).trim();
    if (!q) return;
    setIsSearchingEms(true);
    setEmsSearched(true);
    try {
      const res = await ems.searchDoctors(q);
      setEmsResults(res?.data || []);
    } catch {
      setEmsResults([]);
    } finally {
      setIsSearchingEms(false);
    }
  };

  const formatYear = (dateStr: string) => {
    if (!dateStr) return '-';
    const parsed = moment(dateStr);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : dateStr;
  };

  return (
    <ComponentWithLoading isLoading={!data?.newSyndicateIdURL && !data?.syndicateIdURL}>
      <div className={classes.root}>
        {/* Doctor Info & Uploaded Syndicate Card */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="h6" className={classes.sectionTitle}>
                  {i18n.t('SYNDICATE_ID_VERIFICATION')}
                </Typography>
                <img
                  src={data?.newSyndicateIdURL || data?.syndicateIdURL}
                  alt="Syndicate Card"
                  className={classes.image}
                />
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>{i18n.t('DOCTOR')}:</strong> {data?.user?.name} {data?.arabicName ? `(${data.arabicName})` : ''}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>{i18n.t('SYNDICATE_NUMBER')}:</strong> {data?.syndicateNumber || '-'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Live EMS Syndicate Inquiry Verification */}
          <Grid item xs={12} md={6}>
            <Card className={classes.card} style={{ height: 'calc(100% - 24px)' }}>
              <CardContent>
                <Typography variant="h6" className={classes.sectionTitle}>
                  <VerifiedUserIcon color="primary" />
                  {i18n.t('VERIFY_WITH_EMS')}
                </Typography>

                <div className={classes.emsSearchBox}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder={i18n.t('EMS_SEARCH_PLACEHOLDER')}
                    value={emsQuery}
                    onChange={(e) => setEmsQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        searchEms(emsQuery);
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => searchEms(emsQuery)}
                    disabled={isSearchingEms || !emsQuery.trim()}
                    startIcon={
                      isSearchingEms ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                  >
                    {i18n.t('SEARCH')}
                  </Button>
                </div>

                <Divider style={{ marginBottom: 16 }} />

                <TableContainer component={Paper} elevation={0} style={{ maxHeight: 350 }}>
                  <Table size="small">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell>{i18n.t('SYNDICATE_NUMBER')}</TableCell>
                        <TableCell>{i18n.t('DOCTOR_NAME')}</TableCell>
                        <TableCell>{i18n.t('SPECIALIZATION')}</TableCell>
                        <TableCell>{i18n.t('CONSULTANT_TYPE')}</TableCell>
                        <TableCell>{i18n.t('REGISTRATION_YEAR')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isSearchingEms ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" style={{ padding: 20 }}>
                            <CircularProgress size={24} />
                          </TableCell>
                        </TableRow>
                      ) : emsResults.length > 0 ? (
                        emsResults.map((doc, idx) => (
                          <TableRow key={`${doc.id}-${idx}`}>
                            <TableCell>
                              <Chip
                                label={doc.id}
                                size="small"
                                className={classes.syndicateChip}
                              />
                            </TableCell>
                            <TableCell style={{ fontWeight: 600 }}>{doc.doctorName}</TableCell>
                            <TableCell>{doc.specialization}</TableCell>
                            <TableCell>{doc.consultantType}</TableCell>
                            <TableCell>{formatYear(doc.registrationYear)}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center" style={{ padding: 20, color: '#888' }}>
                            {emsSearched ? i18n.t('NO_EMS_RESULTS') : i18n.t('EMS_SEARCH_PLACEHOLDER')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Button */}
        <Box display="flex" justifyContent="center" mt={3}>
          <Button
            variant="contained"
            color="primary"
            className={classes.approveButton}
            startIcon={<CheckCircleIcon />}
            onClick={async () => {
              await approveSyndicateID(id);
              history.replace('/users/:id/service-providers/doctors');
            }}
          >
            {i18n.t('APPROVE')}
          </Button>
        </Box>
      </div>
    </ComponentWithLoading>
  );
};

export const SyndicateIdVerification = baseScreen(SyndicateIdVerificationComponent, {
  allowedRoles: ['ADMIN', 'NONE'],
});
